import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchField from "../SearchField";
import React, { useEffect, useRef, useState, memo, useCallback } from "react";
import { DeponieDaten, DeponieInfo, DeponieListe } from "../../api";
import {
  useSucheMaterialtransporteDeponie,
  useUpdateMaterialtransportDeponie,
} from "../../client/hooks";
import { useMutation, useQueryClient } from "react-query";
import { Field, useField, useFormikContext, FastField } from "formik";
import { TextField } from "formik-mui";
import { useT } from "../../i18n";
import { useEingangsrechnungContext } from "../../contexts/EingangsrechnungContext";
import WiegescheineSummenzeile from "./WiegescheineSummenzeile";
import { formatAsMoney, formatGermanNumber } from "../../formatterHelpers";

const BaustellenEintragDeponien = memo(
  ({
    baustelleId,
    rechnungId,
    index,
  }: {
    baustelleId: string | undefined;
    rechnungId: string | undefined;
    index: number;
  }) => {
    const [deponien, setDeponien] = useState<DeponieListe>();
    const { t } = useT("eingangsrechnung");
    const [search, setSearch] = useState<string>("");
    const [inputSearch, setInputSearch] = useState<string>("");
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const searchDeponien = useSucheMaterialtransporteDeponie();
    const { mutate } = useSaveDeponie();
    const { values, setFieldValue } = useFormikContext<any>();
    const startDate = values.startDate?.[index];
    const endDate = values.endDate?.[index];

    const [inputStartDate, setInputStartDate] = useState<string>(
      startDate ?? ""
    );
    const [inputEndDate, setInputEndDate] = useState<string>(endDate ?? "");

    useEffect(() => {
      setInputStartDate(startDate ?? "");
    }, [startDate]);

    useEffect(() => {
      setInputEndDate(endDate ?? "");
    }, [endDate]);

    const { getCheckedDeponien, setCheckedDeponien } =
      useEingangsrechnungContext();
    const checkedDeponien = getCheckedDeponien(index);

    const handleSearchChange = useCallback((value: string) => {
      setInputSearch(value);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => setSearch(value), 300);
    }, []);

    const handleDateChange = useCallback(
      (
        setter: React.Dispatch<React.SetStateAction<string>>,
        fieldName: string,
        value: string
      ) => {
        if (value && value.length > 10) {
          return;
        }
        setter(value);
        if (value === "") {
          setFieldValue(fieldName, "");
          return;
        }
        if (value && value.length === 10) {
          const year = parseInt(value.substring(0, 4));
          if (year >= 1000 && year <= 9999) {
            setFieldValue(fieldName, value);
          }
        }
      },
      [setFieldValue]
    );

    const fetchDeponien = useCallback(async () => {
      if (baustelleId) {
        const result = await searchDeponien({
          search: search,
          baustelleId: baustelleId,
          rechnungId: rechnungId,
          startDate: startDate,
          endDate: endDate,
          limit: 250,
        });

        const newCheckedItems: Record<string, boolean> = {};
        result.items.forEach((deponie: DeponieInfo) => {
          if (deponie.rechnungId !== undefined) {
            newCheckedItems[deponie.id] = true;
          }
        });
        setCheckedItems((prev) => ({ ...prev, ...newCheckedItems }));
        setDeponien(result);

        const filteredItems = result.items.filter(
          (item: DeponieInfo) => item.rechnungId !== undefined
        );
        setCheckedDeponien(index, filteredItems);

        setAllCheckedState(
          filteredItems.length === result.items.length &&
            result.items.length > 0
        );
      }
    }, [
      baustelleId,
      search,
      rechnungId,
      startDate,
      endDate,
      searchDeponien,
      index,
      setCheckedDeponien,
    ]);

    useEffect(() => {
      fetchDeponien();
    }, [fetchDeponien]);
    const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
      {}
    );

    const handleToggle = useCallback(
      async (deponie: DeponieInfo) => {
        const currentChecked = checkedItems[deponie.id] || false;
        const newRechnungId = currentChecked ? "" : rechnungId ?? "";
        mutate({
          materialtransportId: deponie.id,
          rechnungId: newRechnungId ?? "",
        });

        const newCheckedItems = {
          ...checkedItems,
          [deponie.id]: !currentChecked,
        };
        setCheckedItems(newCheckedItems);

        setCheckedDeponien(index, (prev) => {
          if (currentChecked) {
            return prev.filter((item) => item.id !== deponie.id);
          } else {
            return [...prev, deponie];
          }
        });

        if (deponien && deponien.items.length > 0) {
          if (currentChecked) {
            setAllCheckedState(false);
          } else {
            const allChecked = deponien.items.every((item) =>
              item.id === deponie.id ? true : newCheckedItems[item.id]
            );
            setAllCheckedState(allChecked);
          }
        }
      },
      [checkedItems, rechnungId, mutate, index, setCheckedDeponien, deponien]
    );

    const [allCheckedState, setAllCheckedState] = useState<boolean>(false);

    const handleToggleAll = useCallback(() => {
      if (!deponien) return;

      const allChecked = deponien.items.every(
        (deponie) => checkedItems[deponie.id]
      );
      const newCheckedState = !allChecked;

      const previousChecked = { ...checkedItems };

      const newCheckedItems = { ...checkedItems };
      deponien.items.forEach((deponie) => {
        newCheckedItems[deponie.id] = newCheckedState;
      });
      setCheckedItems(newCheckedItems);
      setCheckedDeponien(index, newCheckedState ? deponien.items : []);

      deponien.items.forEach((deponie) => {
        if ((previousChecked[deponie.id] ?? false) !== newCheckedState) {
          mutate({
            materialtransportId: deponie.id,
            rechnungId: newCheckedState ? rechnungId ?? "" : "",
          });
        }
      });

      setAllCheckedState(newCheckedState);
    }, [deponien, checkedItems, index, setCheckedDeponien, mutate, rechnungId]);

    return (
      <Accordion>
        <AccordionSummary expandIcon={<ArrowDropDownIcon />}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "90%",
            }}
          >
            <Typography variant="subtitle2">
              {t("landfill-entries-title")}
            </Typography>
            <WiegescheineSummenzeile deponien={checkedDeponien} />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <TableContainer
            component={Paper}
            sx={{
              opacity: 1,
              pointerEvents: "auto",
              background: "#FFFFFF",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ mt: 1 }}
            >
              <SearchField
                value={inputSearch}
                onChange={handleSearchChange}
              ></SearchField>
              <Field
                sx={{ ml: 2 }}
                component={TextField}
                name={`startDate.${index}`}
                label={"von"}
                type="date"
                size={"small"}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: inputEndDate,
                }}
                value={inputStartDate}
                onChange={(e: any) =>
                  handleDateChange(
                    setInputStartDate,
                    `startDate.${index}`,
                    e.target.value
                  )
                }
                onBlur={() => {
                  const year = parseInt(inputStartDate.substring(0, 4));
                  if (year >= 1000 && year <= 9999) {
                    setFieldValue(`startDate.${index}`, inputStartDate);
                  }
                }}
              />
              <Field
                sx={{ ml: 2 }}
                component={TextField}
                name={`endDate.${index}`}
                label={"bis"}
                type="date"
                size={"small"}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: inputStartDate,
                }}
                value={inputEndDate}
                onChange={(e: any) =>
                  handleDateChange(
                    setInputEndDate,
                    `endDate.${index}`,
                    e.target.value
                  )
                }
                onBlur={() => {
                  const year = parseInt(inputEndDate.substring(0, 4));
                  if (year >= 1000 && year <= 9999) {
                    setFieldValue(`endDate.${index}`, inputEndDate);
                  }
                }}
              />
            </Stack>
            <Table size={"small"}>
              <TableHead>
                <TableRow>
                  <TableCell padding="none">
                    <Checkbox
                      checked={allCheckedState}
                      onChange={handleToggleAll}
                    />
                  </TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Abhol- / Abladestelle</TableCell>
                  <TableCell>LieferscheinNr</TableCell>
                  <TableCell sx={{ textAlign: "right" }}>Preis Ges.</TableCell>
                  <TableCell>Menge Ges.</TableCell>
                  <TableCell>Einheiten</TableCell>
                  <TableCell>Fahrten</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deponien ? (
                  deponien.items.map((deponie) => (
                    <TableRow key={deponie.id}>
                      <TableCell padding="none">
                        <Checkbox
                          checked={checkedDeponien.some(
                            ({ id }) => id === deponie.id
                          )}
                          onChange={() => handleToggle(deponie)}
                        />
                      </TableCell>
                      <TableCell padding="none">
                        {new Date(deponie.datum ?? "").toLocaleDateString(
                          undefined,
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }
                        )}
                      </TableCell>
                      <TableCell sx={{ paddingLeft: "15px" }}>
                        {deponie.kurzname}
                      </TableCell>
                      <TableCell sx={{ paddingLeft: "15px" }}>
                        {deponie.lieferscheinNummer}
                      </TableCell>
                      <TableCell
                        sx={{ paddingLeft: "15px", textAlign: "right" }}
                      >
                        {formatAsMoney(deponie.preisGesamt)}
                      </TableCell>
                      <TableCell
                        sx={{ paddingLeft: "15px", textAlign: "right" }}
                      >
                        {formatGermanNumber(deponie.mengeGesamt, 2)}
                      </TableCell>
                      <TableCell>{deponie.einheit}</TableCell>
                      <TableCell
                        sx={{
                          textAlign: "right",
                        }}
                      >
                        {deponie.anzahlFuhren}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <></>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>
    );
  }
);

function useSaveDeponie() {
  const updateDeponie = useUpdateMaterialtransportDeponie();
  const queryClient = useQueryClient();

  return useMutation(
    (input: DeponieDaten) => {
      return updateDeponie(input);
    },
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(["materials"]);
      },
    }
  );
}

export default BaustellenEintragDeponien;
